/**
 * Onboarding step definitions for job seekers and employers
 * Each step targets elements via data-tour attributes
 */

/**
 * Job Seeker Tour Steps (5 steps)
 */
export const seekerTourSteps = [
  {
    target: '[data-tour="seeker-welcome"]',
    content: 'onboarding.seekerTour.welcome',
    placement: 'bottom',
    disableBeacon: true,
    spotlightClicks: true
  },
  {
    target: '[data-tour="seeker-verify-id"]',
    content: 'onboarding.seekerTour.verifyId',
    placement: 'bottom',
    spotlightClicks: true
  },
  {
    target: '[data-tour="seeker-recommendations"]',
    content: 'onboarding.seekerTour.recommendations',
    placement: 'top',
    spotlightClicks: true
  },
  {
    target: '[data-tour="seeker-browse-jobs"]',
    content: 'onboarding.seekerTour.browseJobs',
    placement: 'top',
    spotlightClicks: true
  },
  {
    target: '[data-tour="seeker-applications"]',
    content: 'onboarding.seekerTour.applications',
    placement: 'top',
    spotlightClicks: true
  }
];

/**
 * Employer Tour Steps (6 steps)
 */
export const employerTourSteps = [
  {
    target: '[data-tour="employer-hero"]',
    content: 'onboarding.employerTour.welcome',
    placement: 'bottom',
    disableBeacon: true,
    spotlightClicks: true
  },
  {
    target: '[data-tour="employer-verify-id"]',
    content: 'onboarding.employerTour.verifyId',
    placement: 'bottom',
    spotlightClicks: true
  },
  {
    target: '[data-tour="employer-credits"]',
    content: 'onboarding.employerTour.credits',
    placement: 'left',
    spotlightClicks: true
  },
  {
    target: '[data-tour="employer-post-job"]',
    content: 'onboarding.employerTour.postJob',
    placement: 'bottom',
    spotlightClicks: true
  },
  {
    target: '[data-tour="employer-postings"]',
    content: 'onboarding.employerTour.postings',
    placement: 'top',
    spotlightClicks: true
  },
  {
    target: '[data-tour="employer-referral"]',
    content: 'onboarding.employerTour.referral',
    placement: 'top',
    spotlightClicks: true
  }
];

/**
 * Job Seeker Checklist Items
 * Each item has an id, translation key, action path, and auto-completion logic
 */
export const seekerChecklistItems = [
  {
    id: 'verify_id',
    labelKey: 'onboarding.checklist.seeker.verifyId',
    descriptionKey: 'onboarding.checklist.seeker.verifyIdDesc',
    actionPath: '/profile',
    actionLabelKey: 'onboarding.checklist.verifyNow',
    priority: 'high',
    // Auto-completed when profile.verified === true
    checkCompletion: (profile) => profile?.verified === true
  },
  {
    id: 'complete_profile',
    labelKey: 'onboarding.checklist.seeker.completeProfile',
    descriptionKey: 'onboarding.checklist.seeker.completeProfileDesc',
    actionPath: '/profile',
    actionLabelKey: 'onboarding.checklist.goToProfile',
    priority: 'high',
    // Auto-completed when key profile fields are filled
    checkCompletion: (profile) => {
      if (!profile) return false;
      // Check essential fields for job seeker
      const hasName = !!profile.name;
      const hasPhone = !!profile.phone;
      const hasLocation = !!profile.town || !!profile.location;
      const hasSkills = profile.skills && profile.skills.length > 0;
      const hasBio = !!profile.bio || !!profile.experience;
      return hasName && hasPhone && hasLocation && (hasSkills || hasBio);
    }
  },
  {
    id: 'browse_jobs',
    labelKey: 'onboarding.checklist.seeker.browseJobs',
    descriptionKey: 'onboarding.checklist.seeker.browseJobsDesc',
    actionPath: '/jobs',
    actionLabelKey: 'onboarding.checklist.browseNow',
    priority: 'medium',
    // Manual completion or track via storage
    checkCompletion: () => false
  },
  {
    id: 'first_application',
    labelKey: 'onboarding.checklist.seeker.firstApplication',
    descriptionKey: 'onboarding.checklist.seeker.firstApplicationDesc',
    actionPath: '/jobs',
    actionLabelKey: 'onboarding.checklist.findJob',
    priority: 'medium',
    // Auto-completed when user has at least 1 application
    checkCompletion: (profile, stats) => (stats?.applicationsCount || 0) > 0
  },
  {
    id: 'check_applications',
    labelKey: 'onboarding.checklist.seeker.checkApplications',
    descriptionKey: 'onboarding.checklist.seeker.checkApplicationsDesc',
    actionPath: '/dashboard',
    actionLabelKey: 'onboarding.checklist.viewApplications',
    priority: 'low',
    // Manual completion
    checkCompletion: () => false
  }
];

/**
 * Employer Checklist Items
 * ID verification is REQUIRED before posting jobs
 */
export const employerChecklistItems = [
  {
    id: 'verify_id',
    labelKey: 'onboarding.checklist.employer.verifyId',
    descriptionKey: 'onboarding.checklist.employer.verifyIdDesc',
    actionPath: '/profile',
    actionLabelKey: 'onboarding.checklist.verifyNow',
    priority: 'critical',
    required: true,
    // Auto-completed when profile.verified === true
    checkCompletion: (profile) => profile?.verified === true
  },
  {
    id: 'complete_profile',
    labelKey: 'onboarding.checklist.employer.completeProfile',
    descriptionKey: 'onboarding.checklist.employer.completeProfileDesc',
    actionPath: '/profile',
    actionLabelKey: 'onboarding.checklist.goToProfile',
    priority: 'high',
    // Auto-completed when profile completeness >= 90%
    checkCompletion: (profile, stats) => (stats?.profileCompleteness || 0) >= 90
  },
  {
    id: 'first_job',
    labelKey: 'onboarding.checklist.employer.firstJob',
    descriptionKey: 'onboarding.checklist.employer.firstJobDesc',
    actionPath: '/post-job',
    actionLabelKey: 'onboarding.checklist.postNow',
    priority: 'high',
    // Auto-completed when user has at least 1 job posted
    checkCompletion: (profile, stats) => (stats?.jobsCount || 0) > 0
  },
  {
    id: 'review_applicant',
    labelKey: 'onboarding.checklist.employer.reviewApplicant',
    descriptionKey: 'onboarding.checklist.employer.reviewApplicantDesc',
    actionPath: '/dashboard',
    actionLabelKey: 'onboarding.checklist.viewApplicants',
    priority: 'medium',
    // Auto-completed when user has viewed at least 1 application
    checkCompletion: (profile, stats) => (stats?.viewedApplicationsCount || 0) > 0
  },
  {
    id: 'mark_hired',
    labelKey: 'onboarding.checklist.employer.markHired',
    descriptionKey: 'onboarding.checklist.employer.markHiredDesc',
    actionPath: '/dashboard',
    actionLabelKey: 'onboarding.checklist.viewJobs',
    priority: 'low',
    // Auto-completed when user has at least 1 filled job
    checkCompletion: (profile, stats) => (stats?.filledJobsCount || 0) > 0
  }
];

/**
 * Get tour steps for a role
 */
export const getTourSteps = (role) => {
  return role === 'employer' ? employerTourSteps : seekerTourSteps;
};

/**
 * Get checklist items for a role
 */
export const getChecklistItems = (role) => {
  return role === 'employer' ? employerChecklistItems : seekerChecklistItems;
};

/**
 * Get priority badge color
 */
export const getPriorityColor = (priority) => {
  switch (priority) {
    case 'critical':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'high':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'medium':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'low':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};
